import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, AlertTriangle, Rocket, Loader2, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { PortfolioData } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

const healthColors: Record<string, string> = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft', active: 'Active', on_hold: 'On Hold', completed: 'Completed', churned: 'Churned',
};

export default function PortfolioDashboard() {
  const { t } = useAppTranslation();
  const [data, setData] = useState<PortfolioData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setData(await api.getPortfolio()); }
      catch (err) { console.error('Error fetching portfolio:', err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!data) return <p className="text-gray-400 text-center py-12">Unable to load portfolio data</p>;

  const activeCount = data.by_status.find(s => s.status === 'active')?.c || 0;
  const completedCount = data.by_status.find(s => s.status === 'completed')?.c || 0;
  const atRiskCount = data.by_health.find(h => h.health === 'red')?.c || 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t('onboarding.portfolio.title', 'Portfolio Dashboard')}</h2>
        <p className="text-sm text-gray-500">Management overview across all customer onboardings</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <Rocket className="h-5 w-5 text-blue-600 mb-1" />
            <p className="text-sm text-gray-600">Active</p>
            <p className="text-2xl font-bold">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-5 pb-4">
            <TrendingUp className="h-5 w-5 text-green-600 mb-1" />
            <p className="text-sm text-green-700">Completed</p>
            <p className="text-2xl font-bold text-green-800">{completedCount}</p>
          </CardContent>
        </Card>
        <Card className={atRiskCount > 0 ? 'bg-red-50 border-red-200' : ''}>
          <CardContent className="pt-5 pb-4">
            <AlertTriangle className={`h-5 w-5 mb-1 ${atRiskCount > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600">At Risk</p>
            <p className={`text-2xl font-bold ${atRiskCount > 0 ? 'text-red-700' : ''}`}>{atRiskCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <AlertTriangle className="h-5 w-5 text-orange-500 mb-1" />
            <p className="text-sm text-gray-600">Open Items</p>
            <p className="text-2xl font-bold">{data.open_items_count}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Status */}
        <Card>
          <CardHeader><CardTitle className="text-sm">By Status</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.by_status.map((s) => (
                <div key={s.status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{statusLabels[s.status] || s.status}</span>
                  <span className="font-semibold">{s.c}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Health */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Health Distribution</CardTitle></CardHeader>
          <CardContent>
            {data.by_health.length === 0 ? (
              <p className="text-sm text-gray-400">No active plans</p>
            ) : (
              <div className="flex gap-2">
                {data.by_health.map((h) => {
                  const total = data.by_health.reduce((s, x) => s + x.c, 0);
                  const pct = total > 0 ? Math.round((h.c / total) * 100) : 0;
                  return (
                    <div key={h.health} className={`rounded-lg p-3 text-center flex-1 ${healthColors[h.health] || 'bg-gray-100'}`}>
                      <p className="text-2xl font-bold">{h.c}</p>
                      <p className="text-xs capitalize">{h.health} ({pct}%)</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Phase (funnel) */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Phase Funnel (Active Plans)</CardTitle></CardHeader>
          <CardContent>
            {data.by_phase.length === 0 ? (
              <p className="text-sm text-gray-400">No active plans</p>
            ) : (
              <div className="space-y-2">
                {data.by_phase.map((ph) => {
                  const maxCount = Math.max(...data.by_phase.map(p => p.c), 1);
                  const pct = Math.round((ph.c / maxCount) * 100);
                  return (
                    <div key={ph.current_phase_id} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">{ph.phase_name || 'Not set'}</span>
                        <span className="text-gray-500">{ph.c} · avg {ph.avg_days_in_phase ?? '—'}d</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CSM Capacity */}
        <Card>
          <CardHeader><CardTitle className="text-sm">CSM Capacity</CardTitle></CardHeader>
          <CardContent>
            {data.by_csm.length === 0 ? (
              <p className="text-sm text-gray-400">No active plans</p>
            ) : (
              <div className="space-y-2">
                {data.by_csm.map((csm) => (
                  <div key={csm.csm_user_id ?? 'unassigned'} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{csm.csm_name || 'Unassigned'}</span>
                    </div>
                    <Badge variant="outline">{csm.c} plans</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming go-lives */}
      {data.upcoming_go_lives.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Upcoming Go-Lives (Next 30 Days)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.upcoming_go_lives.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-sm">{p.customer_org_name}</p>
                      <p className="text-xs text-gray-500">{p.target_go_live_date ? new Date(p.target_go_live_date).toLocaleDateString() : '—'}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={healthColors[p.health] || ''}>{p.health}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
